// components/ApiPlayground.tsx
import React, { useEffect, useState } from "react";
import { type ApiEndpoint } from "../apiDocsSchema";

type ApiPlaygroundProps = {
  selectedEndpoint: ApiEndpoint | null;
};

const LANGUAGES = [
  "Curl",
  "Ruby",
  "Python",
  "PHP",
  "Java",
  "Node",
  "Go",
  ".NET",
] as const;

type Lang = (typeof LANGUAGES)[number];

type GenericRequest = {
  method: string;
  url: string;
  headers: Record<string, string>;
  bodyText: string | null;
};

// Mini syntax highlighter estilo VS Code (para JSON/texto)
const syntaxHighlight = (text: string): string => {
  if (!text) return "";

  // Escapa HTML
  let json = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Destaca JSON básico (chaves, strings, números, boolean, null)
  json = json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = "json-number";

      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = "json-key";
        } else {
          cls = "json-string";
        }
      } else if (/true|false/.test(match)) {
        cls = "json-boolean";
      } else if (/null/.test(match)) {
        cls = "json-null";
      }

      return `<span class="${cls}">${match}</span>`;
    }
  );

  return json;
};

const ApiPlayground: React.FC<ApiPlaygroundProps> = ({ selectedEndpoint }) => {
  const [baseUrl] = useState("https://api.eva-googlemarine.com");
  const [authToken, setAuthToken] = useState("SEU_ACCESS_TOKEN_AQUI");

  const [reqQuery, setReqQuery] = useState<Record<string, string>>({});
  const [reqBodyText, setReqBodyText] = useState<string>("{}");
  const [responseText, setResponseText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string>("");

  // Linguagem ativa na aba
  const [activeLang, setActiveLang] = useState<Lang>("Curl");

  // Sempre que troca o endpoint selecionado, ajusta os defaults do playground
  useEffect(() => {
    if (!selectedEndpoint) return;

    const qDefaults = selectedEndpoint.playgroundDefaults?.query || {};
    setReqQuery(qDefaults);

    if (selectedEndpoint.playgroundDefaults?.body) {
      setReqBodyText(
        JSON.stringify(selectedEndpoint.playgroundDefaults.body, null, 2)
      );
    } else {
      setReqBodyText(
        ["POST", "PUT", "PATCH"].includes(selectedEndpoint.method)
          ? "{\n  \n}"
          : "{}"
      );
    }

    setResponseText("");
    setErrorText("");
    setActiveLang("Curl"); // sempre começa em cURL ao mudar de endpoint
  }, [selectedEndpoint]);

  const handleChangeQueryParam = (name: string, value: string) => {
    setReqQuery((prev) => ({ ...prev, [name]: value }));
  };

  const buildUrlWithQuery = (ep: ApiEndpoint) => {
    let path = ep.path;

    // substitui params de path se tiver
    ep.params
      ?.filter((p) => p.in === "path")
      .forEach((p) => {
        const val = reqQuery[p.name] || `{${p.name}}`;
        path = path.replace(`{${p.name}}`, encodeURIComponent(val));
      });

    const searchParams = new URLSearchParams();
    ep.params
      ?.filter((p) => p.in === "query")
      .forEach((p) => {
        const v = reqQuery[p.name];
        if (v != null && v !== "") searchParams.append(p.name, v);
      });

    const qs = searchParams.toString();
    return qs ? `${baseUrl}${path}?${qs}` : `${baseUrl}${path}`;
  };

  // -------------------------
  // Monta um GenericRequest (base para todos os snippets)
  // -------------------------
  const buildGenericRequest = (
    ep: ApiEndpoint | null
  ): GenericRequest | null => {
    if (!ep) return null;

    const url = buildUrlWithQuery(ep);

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Só adiciona Authorization se o endpoint exigir auth
    if (ep.authRequired) {
      const tokenPreview = authToken || "SEU_ACCESS_TOKEN_AQUI";
      headers["Authorization"] = `Bearer ${tokenPreview}`;
    }

    let bodyText: string | null = null;
    if (["POST", "PUT", "PATCH"].includes(ep.method)) {
      bodyText = reqBodyText && reqBodyText.trim() ? reqBodyText : "{}";
    }

    return {
      method: ep.method.toUpperCase(),
      url,
      headers,
      bodyText,
    };
  };

  // -------------------------
  // GERADORES DE SNIPPET
  // -------------------------
  const generateCurlSnippet = (req: GenericRequest): string => {
    const lines: string[] = [`curl -X ${req.method} "${req.url}"`];

    Object.entries(req.headers).forEach(([k, v]) => {
      lines.push(`  -H "${k}: ${v.replace(/"/g, '\\"')}"`);
    });

    if (req.bodyText) {
      lines.push(`  -d '${req.bodyText}'`);
    }

    return lines.join(" \\\n");
  };

  const generatePythonSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;
    const headerLines = Object.entries(req.headers)
      .map(([k, v]) => `    "${k}": "${v.replace(/"/g, '\\"')}",`)
      .join("\n");

    const bodyPart = hasBody ? `payload = ${req.bodyText}\n\n` : "";

    const payloadArg = hasBody ? ", json=payload" : "";

    return [
      "import requests",
      "",
      `url = "${req.url}"`,
      "",
      "headers = {",
      headerLines,
      "}",
      "",
      bodyPart +
        `response = requests.${req.method.toLowerCase()}(url, headers=headers${payloadArg})`,
      "print(response.status_code)",
      "print(response.text)",
    ].join("\n");
  };

  const generateRubySnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;
    const headerLines = Object.entries(req.headers)
      .map(([k, v]) => `request["${k}"] = "${v.replace(/"/g, '\\"')}"`)
      .join("\n");

    const methodClass = (() => {
      switch (req.method) {
        case "POST":
          return "Net::HTTP::Post";
        case "PUT":
          return "Net::HTTP::Put";
        case "PATCH":
          return "Net::HTTP::Patch";
        case "DELETE":
          return "Net::HTTP::Delete";
        default:
          return "Net::HTTP::Get";
      }
    })();

    const bodyLine = hasBody ? `request.body = ${req.bodyText}\n` : "";

    return [
      'require "net/http"',
      'require "uri"',
      'require "json"',
      "",
      `url = URI("${req.url}")`,
      "http = Net::HTTP.new(url.host, url.port)",
      'http.use_ssl = (url.scheme == "https")',
      "",
      `request = ${methodClass}.new(url)`,
      headerLines,
      hasBody ? "" : "",
      bodyLine,
      "response = http.request(request)",
      "puts response.code",
      "puts response.body",
    ].join("\n");
  };

  const generatePHPSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;

    const headerLines = Object.entries(req.headers)
      .map(([k, v]) => `"${k}: ${v.replace(/"/g, '\\"')}"`)
      .join(",\n        ");

    const bodyPart = hasBody
      ? `curl_setopt($ch, CURLOPT_POSTFIELDS, ${req.bodyText});\n`
      : "";

    const customRequest =
      req.method === "GET"
        ? ""
        : `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${req.method}");\n`;

    return [
      "<?php",
      "",
      `$url = "${req.url}";`,
      "",
      "$ch = curl_init();",
      "curl_setopt($ch, CURLOPT_URL, $url);",
      "curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);",
      "curl_setopt($ch, CURLOPT_HTTPHEADER, [",
      `        ${headerLines}`,
      "]);",
      customRequest + bodyPart,
      "",
      "$response = curl_exec($ch);",
      "$statusCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);",
      "curl_close($ch);",
      "",
      'echo $statusCode . "\\n";',
      "echo $response;",
      "",
      "?>",
    ].join("\n");
  };

  const generateJavaSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;

    const headerLines = Object.entries(req.headers)
      .map(
        ([k, v]) =>
          `        connection.setRequestProperty("${k}", "${v.replace(
            /"/g,
            '\\"'
          )}");`
      )
      .join("\n");

    const bodyPart = hasBody
      ? [
          "        connection.setDoOutput(true);",
          "        try(OutputStream os = connection.getOutputStream()) {",
          `            byte[] input = "${req.bodyText}"
                    .getBytes("utf-8");`.replace(/\s+\.getBytes/, ".getBytes"),
          "            os.write(input, 0, input.length);",
          "        }",
        ].join("\n")
      : "";

    const methodLine =
      req.method === "GET"
        ? ""
        : `        connection.setRequestMethod("${req.method}");`;

    return [
      "import java.io.BufferedReader;",
      "import java.io.InputStreamReader;",
      "import java.io.OutputStream;",
      "import java.net.HttpURLConnection;",
      "import java.net.URL;",
      "",
      "public class Main {",
      "    public static void main(String[] args) throws Exception {",
      `        URL url = new URL("${req.url}");`,
      "        HttpURLConnection connection = (HttpURLConnection) url.openConnection();",
      methodLine,
      headerLines,
      bodyPart,
      "",
      "        int statusCode = connection.getResponseCode();",
      "        BufferedReader in = new BufferedReader(new InputStreamReader(connection.getInputStream()));",
      "        String inputLine;",
      "        StringBuilder content = new StringBuilder();",
      "        while ((inputLine = in.readLine()) != null) {",
      "            content.append(inputLine);",
      "        }",
      "        in.close();",
      "        connection.disconnect();",
      "",
      "        System.out.println(statusCode);",
      "        System.out.println(content.toString());",
      "    }",
      "}",
    ].join("\n");
  };

  const generateNodeSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;

    const headerLines = Object.entries(req.headers)
      .map(([k, v]) => `      "${k}": "${v.replace(/"/g, '\\"')}"`)
      .join(",\n");

    const bodyPart = hasBody ? `  body: ${req.bodyText},\n` : "";

    return [
      'import fetch from "node-fetch";',
      "",
      `const url = "${req.url}";`,
      "",
      "const options = {",
      `  method: "${req.method}",`,
      "  headers: {",
      headerLines,
      "  },",
      bodyPart.trimEnd(),
      "};",
      "",
      "async function main() {",
      "  const response = await fetch(url, options);",
      "  const text = await response.text();",
      "  console.log(response.status, response.statusText);",
      "  console.log(text);",
      "}",
      "",
      "main().catch(console.error);",
    ].join("\n");
  };

  const generateGoSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;

    const headerLines = Object.entries(req.headers)
      .map(
        ([k, v]) => `    req.Header.Set("${k}", "${v.replace(/"/g, '\\"')}")`
      )
      .join("\n");

    const bodyExpr = hasBody
      ? `bytes.NewBuffer([]byte(\`${req.bodyText}\`))`
      : "nil";

    return [
      "package main",
      "",
      "import (",
      '    "bytes"',
      '    "fmt"',
      '    "io"',
      '    "net/http"',
      ")",
      "",
      "func main() {",
      `    url := "${req.url}"`,
      `    req, err := http.NewRequest("${req.method}", url, ${bodyExpr})`,
      "    if err != nil {",
      "        panic(err)",
      "    }",
      headerLines,
      "",
      "    res, err := http.DefaultClient.Do(req)",
      "    if err != nil {",
      "        panic(err)",
      "    }",
      "    defer res.Body.Close()",
      "",
      "    body, err := io.ReadAll(res.Body)",
      "    if err != nil {",
      "        panic(err)",
      "    }",
      "    fmt.Println(res.StatusCode)",
      "    fmt.Println(string(body))",
      "}",
    ].join("\n");
  };

  const generateDotNetSnippet = (req: GenericRequest): string => {
    const hasBody = !!req.bodyText;

    const headerLines = Object.entries(req.headers)
      .map(
        ([k, v]) =>
          `        request.Headers.TryAddWithoutValidation("${k}", "${v.replace(
            /"/g,
            '\\"'
          )}");`
      )
      .join("\n");

    const bodyPart = hasBody
      ? [
          `        var content = new StringContent("${req.bodyText}".Replace("\\n", ""), System.Text.Encoding.UTF8, "application/json");`,
          "        request.Content = content;",
        ].join("\n")
      : "";

    return [
      "using System;",
      "using System.Net.Http;",
      "using System.Threading.Tasks;",
      "",
      "class Program {",
      "    static async Task Main(string[] args) {",
      "        var client = new HttpClient();",
      `        var request = new HttpRequestMessage(HttpMethod.${
        req.method.toUpperCase() === "GET"
          ? "Get"
          : req.method.charAt(0) + req.method.slice(1).toLowerCase()
      }, "${req.url}");`,
      headerLines,
      bodyPart,
      "",
      "        var response = await client.SendAsync(request);",
      "        var body = await response.Content.ReadAsStringAsync();",
      "        Console.WriteLine((int)response.StatusCode);",
      "        Console.WriteLine(body);",
      "    }",
      "}",
    ].join("\n");
  };

  // Escolhe o gerador com base na linguagem ativa
  const generateSnippet = (lang: Lang, ep: ApiEndpoint | null): string => {
    const req = buildGenericRequest(ep);
    if (!req) return "";

    switch (lang) {
      case "Curl":
        return generateCurlSnippet(req);
      case "Python":
        return generatePythonSnippet(req);
      case "Ruby":
        return generateRubySnippet(req);
      case "PHP":
        return generatePHPSnippet(req);
      case "Java":
        return generateJavaSnippet(req);
      case "Node":
        return generateNodeSnippet(req);
      case "Go":
        return generateGoSnippet(req);
      case ".NET":
        return generateDotNetSnippet(req);
      default:
        return generateCurlSnippet(req);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedEndpoint) return;
    setLoading(true);
    setErrorText("");
    setResponseText("");

    try {
      const url = buildUrlWithQuery(selectedEndpoint);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Aqui você decide se quer mandar Authorization só se authRequired:
      if (selectedEndpoint.authRequired && authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      let body: BodyInit | undefined;
      if (["POST", "PUT", "PATCH"].includes(selectedEndpoint.method)) {
        try {
          body = reqBodyText ? JSON.stringify(JSON.parse(reqBodyText)) : "{}";
        } catch (err: any) {
          setErrorText("JSON do corpo inválido.");
          setLoading(false);
          return;
        }
      }

      const res = await fetch(url, {
        method: selectedEndpoint.method,
        headers,
        body,
      });

      const text = await res.text();
      let pretty = text;
      try {
        const json = JSON.parse(text);
        pretty = JSON.stringify(json, null, 2);
      } catch {
        // não era JSON, mantém texto cru
      }

      setResponseText(`Status: ${res.status} ${res.statusText}\n\n${pretty}`);
    } catch (err: any) {
      setErrorText(String(err));
    } finally {
      setLoading(false);
    }
  };

  // Path grande para o cabeçalho (ex.: /auth/login)
  const headerPath =
    selectedEndpoint?.path?.startsWith("http") || !selectedEndpoint?.path
      ? selectedEndpoint?.path || ""
      : selectedEndpoint.path;

  const displayPath =
    headerPath && headerPath.startsWith("http")
      ? new URL(headerPath).pathname
      : headerPath || "";

  // URL “ao vivo” para o Ambiente de Testes
  const liveUrl =
    selectedEndpoint && selectedEndpoint.path
      ? buildUrlWithQuery(selectedEndpoint)
      : `${baseUrl}/`;

  // Headers que serão usados na requisição (para exibir no Ambiente de Testes)
  const previewHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (selectedEndpoint?.authRequired && authToken) {
    previewHeaders["Authorization"] = `Bearer ${authToken}`;
  }

  // Exemplo de resposta vindo do endpoint (primeiro example com "response")
  const exampleResponseRaw = selectedEndpoint?.examples?.find(
    (ex) => ex.response
  )?.response;
  const exampleResponseText = exampleResponseRaw
    ? JSON.stringify(exampleResponseRaw, null, 2)
    : "";

  return (
    <aside
      className="api-playground-root"
      style={{
        borderLeft: "1px solid #2a2d30",
        boxSizing: "border-box",
        background: "#2d3134",
        display: "flex",
        flexDirection: "column",
        color: "#909b7e",
        overflowY: "auto", // scroll vertical próprio
        maxHeight: "100vh",
      }}
    >
      {/* Barra de linguagens no topo */}
      <div
        style={{
          background: "#262729",
          padding: "10px 40px",
          borderBottom: "1px solid #1f2123",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            maxWidth: 520,
            margin: "0 auto",
            gap: 12,
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang === activeLang;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLang(lang)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isActive ? "#ffffff" : "#9ca3af",
                  textAlign: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  padding: "4px 0",
                  borderBottom: isActive
                    ? "2px solid #D6D7D9FF"
                    : "2px solid transparent",
                }}
              >
                {lang}
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo principal do playground */}
      <div
        style={{
          padding: "24px 40px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flex: 1,
        }}
      >
        {selectedEndpoint ? (
          <>
            {/* Campo de Access Token */}

            {/* Cabeçalho com o path grande */}
            <div
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: "#ffffff",
                marginBottom: 4,
              }}
            >
              {displayPath || "/"}
            </div>

            {/* 1) Exemplo de Requisição (snippet em linguagem ativa) */}
            {/* 1) Exemplo de Requisição (snippet em linguagem ativa) */}
            <div style={{ marginTop: 20, marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 400,
                  marginBottom: 10,
                  color: "#ffffff",
                }}
              >
                Exemplo de Requisição ({activeLang})
              </div>

              {/* SNIPPET */}
              <pre
                className="api-playground-code"
                style={{
                  background: "#272b2e",
                  padding: 16,
                  borderRadius: 8,
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "#d4d4d4",
                  overflowX: "auto",
                  overflowY: "hidden",
                  margin: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  whiteSpace: "pre",
                }}
              >
                {generateSnippet(activeLang, selectedEndpoint)}
              </pre>
            </div>

            {/* ⬇⬇⬇  CAMPO DE ACCESS TOKEN AGORA AQUI  ⬇⬇⬇ */}
            {/* ⬇⬇⬇  CAMPO DE ACCESS TOKEN SÓ SE O ENDPOINT EXIGIR AUTH  ⬇⬇⬇ */}
            {selectedEndpoint?.authRequired && (
              <div
                style={{
                  marginBottom: 0,
                  marginTop: -4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <label
                  style={{
                    fontSize: 11,
                    color: "#d1d5db",
                  }}
                >
                  Access Token (Bearer)
                </label>

                <input
                  type="text"
                  value={authToken}
                  onChange={(e) => setAuthToken(e.target.value)}
                  placeholder="Cole aqui apenas o token, sem 'Bearer '"
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #3b3f43",
                    fontSize: 11,
                    background: "#1f2325",
                    color: "#e5e7eb",
                    outline: "none",
                    boxShadow: "none",
                    boxSizing: "border-box",
                    fontFamily: "monospace",
                  }}
                />
              </div>
            )}

            {/* 2) Exemplo de Resposta (opcional, vindo do endpoint) */}
            {exampleResponseText && (
              <div style={{ marginTop: 16, marginBottom: 10 }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    marginBottom: 10,
                    color: "#ffffff",
                  }}
                >
                  Exemplo de Resposta
                </div>
                <pre
                  className="api-playground-code"
                  style={{
                    background: "#272b2e",
                    color: "#d4d4d4",
                    padding: 16,
                    borderRadius: 8,
                    fontSize: 11,
                    overflowX: "auto",
                    overflowY: "hidden",
                    margin: 0,
                    boxSizing: "border-box",
                    fontFamily: "monospace",
                    whiteSpace: "pre",
                  }}
                >
                  <code
                    dangerouslySetInnerHTML={{
                      __html: syntaxHighlight(exampleResponseText),
                    }}
                  />
                </pre>
              </div>
            )}

            {/* 3) Ambiente de Testes COMPLETO (URL + Headers + Body) */}
            {["POST", "PUT", "PATCH"].includes(selectedEndpoint.method) && (
              <div style={{ marginTop: 20 }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    marginBottom: 10,
                    color: "#ffffff",
                  }}
                >
                  Ambiente de Testes
                </div>

                <div
                  style={{
                    background: "#272b2e",
                    borderRadius: 8,
                    padding: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  {/* Método + URL */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        marginBottom: 4,
                      }}
                    >
                      URL
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Badge do método */}
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 999,
                          color: "#f9fafb",
                        }}
                      >
                        {selectedEndpoint.method}
                      </span>

                      <code
                        style={{
                          fontFamily: "monospace",
                          fontSize: 11,
                          color: "#e5e7eb",
                          wordBreak: "break-all",
                        }}
                      >
                        {liveUrl}
                      </code>
                    </div>
                  </div>

                  {/* Headers */}
                  <div>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Headers
                    </span>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: "#e5e7eb",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {Object.entries(previewHeaders).map(([k, v]) => (
                        <div key={k}>
                          <span style={{ color: "#9ca3af" }}>{k}:</span>{" "}
                          <span>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Body */}
                  <div>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Body (JSON)
                    </span>
                    <textarea
                      value={reqBodyText}
                      onChange={(e) => setReqBodyText(e.target.value)}
                      style={{
                        width: "100%",
                        minHeight: 160,
                        fontFamily: "monospace",
                        fontSize: 12,
                        padding: 12,
                        borderRadius: 6,
                        border: "1px solid #3b3f43",
                        outline: "none",
                        boxShadow: "none",
                        background: "#1f2325",
                        color: "#e5e7eb",
                        resize: "vertical",
                        margin: 0,
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 4) Parâmetros (query / path) */}
            {selectedEndpoint.params?.some(
              (p) => p.in === "query" || p.in === "path"
            ) && (
              <div style={{ marginTop: 20 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "#ffffff",
                  }}
                >
                  Parâmetros (query / path)
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {selectedEndpoint.params
                    .filter((p) => p.in === "query" || p.in === "path")
                    .map((p) => (
                      <div key={p.in + p.name}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#c4c4c4",
                            marginBottom: 2,
                          }}
                        >
                          {p.name}{" "}
                          <span style={{ color: "#9ca3af" }}>({p.in})</span>
                        </div>
                        <input
                          type="text"
                          value={reqQuery[p.name] ?? ""}
                          onChange={(e) =>
                            handleChangeQueryParam(p.name, e.target.value)
                          }
                          placeholder={p.description}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #3b3f43",
                            fontSize: 11,
                            background: "#25282b",
                            color: "#e5e7eb",
                            outline: "none",
                            boxShadow: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* 5) Botão rodar + Resposta dinâmica */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <button
                onClick={handleSendRequest}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 4,
                  border: "none",
                  background: "#272b2e",
                  color: "white",
                  fontSize: 13,
                  fontWeight: 400,
                  cursor: loading ? "default" : "pointer",
                  marginBottom: 20,
                  outline: "none",
                }}
              >
                {loading ? "Enviando..." : "Rodar requisição"}
              </button>

              {errorText && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#fca5a5",
                    marginBottom: 4,
                  }}
                >
                  {errorText}
                </div>
              )}

              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                    color: "#ffffff",
                  }}
                >
                  Resposta
                </div>
                <pre
                  className="api-playground-code"
                  style={{
                    background: "#272b2e",
                    color: "#d4d4d4",
                    padding: 16,
                    borderRadius: 8,
                    fontSize: 11,
                    overflowX: "auto", // só horizontal
                    overflowY: "hidden", // sem scroll vertical interno
                    margin: 0,
                    boxSizing: "border-box",
                    fontFamily: "monospace",
                    whiteSpace: "pre",
                  }}
                >
                  <code
                    dangerouslySetInnerHTML={{
                      __html: syntaxHighlight(
                        responseText ||
                          "// Rode a requisição para ver a resposta aqui."
                      ),
                    }}
                  />
                </pre>
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            Selecione um endpoint para usar o playground.
          </div>
        )}
      </div>

      {/* Estilos inline para as classes de highlight + scrollbar customizada */}
      <style>
        {`
          .json-key {
            color: #9cdcfe;
          }
          .json-string {
            color: #ce9178;
          }
          .json-number {
            color: #b5cea8;
          }
          .json-boolean {
            color: #569cd6;
          }
          .json-null {
            color: #569cd6;
          }

          /* Scrollbar geral do playground (barra cinza) */
          .api-playground-root {
            scrollbar-width: thin;
            scrollbar-color: #9ca3af #3b3f43;
          }

          .api-playground-root::-webkit-scrollbar {
            width: 8px;
          }

          .api-playground-root::-webkit-scrollbar-track {
            background: #3b3f43;
          }

          .api-playground-root::-webkit-scrollbar-thumb {
            background-color: #9ca3af;
            border-radius: 999px;
          }

          .api-playground-root::-webkit-scrollbar-thumb:hover {
            background-color: #d1d5db;
          }

          /* Scrollbar escuro, estilo VS Code, apenas onde tem a classe */
          .api-playground-code {
            scrollbar-width: thin;
            scrollbar-color: #4b5563 #111827;
          }

          .api-playground-code::-webkit-scrollbar {
            height: 6px;
          }

          .api-playground-code::-webkit-scrollbar-track {
            background: #272b2e;
          }

          .api-playground-code::-webkit-scrollbar-thumb {
            background: #4b5563;
            border-radius: 999px;
          }

          .api-playground-code::-webkit-scrollbar-thumb:hover {
            background: #6b7280;
          }
        `}
      </style>
    </aside>
  );
};

export default ApiPlayground;
